import { useEffect, useMemo } from "react";
import { isSameWeek } from "date-fns";
import { useLocalStore } from "mobx-react";
import { useFormStore } from "@modules/formx-react/useFormStore";
import {
  FullTravelRequestFragment,
  useCreateTravelRequestMutation,
  BookingHospitalityGet
} from "@graphql";
import { useCostCenters, useLocale, useSnackbars } from "@hooks";
import { useNavigation } from "@navigation";
import { TravelRequest } from "./TravelRequest";
import { TravelRequestFormStore } from "./TravelRequestFormStore";
import { TransportationType, TicketDifficultyLevel } from "@bonliva-crm/shared";
import { ConsultantSelectorValue } from "@containers/VirtualSelector/ConsultantSelector/ConsultantSelector";
import { ProjectSelectorStore } from "@bookings/ProjectSelector/ProjectSelector";
import { getOptions } from "@common/CostCenterSelect";

export type TravelRequestFormData = TravelRequest &
  Pick<FullTravelRequestFragment, "_id" | "travelPreferences">;

export function useTravelRequestForm(
  bookingGroupId: string | undefined,
  initialData: FullTravelRequestFragment | null,
  readonly: boolean,
  isNew: boolean
) {
  const { showSuccess, showError } = useSnackbars();
  const nav = useNavigation();
  const localStore = useLocalStore(() => new TravelRequestFormStore(bookingGroupId));
  const { locale } = useLocale();
  const [send] = useCreateTravelRequestMutation({
    refetchQueries: [BookingHospitalityGet.OperationName]
  });

  const { costCenters } = useCostCenters();
  const costCenterOptions = useMemo(
    () => getOptions(costCenters.filter((x) => x.IsActive)),
    [costCenters]
  );

  const formatInitialValues = (
    data: FullTravelRequestFragment | null
  ): TravelRequestFormData => {
    if (!data) {
      return null;
    }

    return {
      ...data,
      id: data._id,
      deadline: data.deadline ?? new Date(),
      isFlexible: data.isFlexible ? "true" : "false",
      isCancelable: data.isCancelable ? "true" : "false",
      ticketDifficultyLevel: data.ticketDifficultyLevel ?? null,
      isReschedule: data.isReschedule ?? false,
      rescheduleComment: data.rescheduleComment ?? ""
    } as TravelRequestFormData;
  };

  const form = useFormStore<TravelRequest>({
    initialValues: TravelRequest.Create(formatInitialValues(initialData)),
    classValidatorClassType: TravelRequest,
    exposeAs: "travelRequestForm",
    initialDisabled: readonly,
    validate: (store) => TravelRequest.ValidateCustom(store.values, !!bookingGroupId),
    onSetValue: ({ store, name }) => {
      const pathArr = name.split(".");

      if (pathArr.at(-1) === "country") {
        const newPath = pathArr.slice(0, -1).join(".");

        store.setValue(`${newPath}.city`, "");
      }

      if (pathArr.at(-1) === "rideType") {
        const newPath = pathArr.slice(0, -1).join(".");

        store.setValue(`${newPath}.dir2.date`, null);
      }
    },
    onSubmit: () => {
      const { bookingData } = localStore;
      const bookingIds = bookingData
        ? bookingData.bookings
            .filter((x) =>
              form.values.rides?.some(
                (ride) =>
                  isSameWeek(new Date(x.Week), ride.dir1?.date) ||
                  isSameWeek(new Date(x.Week), ride.dir2?.date)
              )
            )
            .map((x) => x._id)
        : [];

      const { isFlexible, isCancelable, luggageType, ...rest } = form.values;
      const isFlight = form.values.rides.some((ride) =>
        ride.transportationType?.includes(TransportationType.Flight)
      );

      return send({
        variables: {
          bookingGroupId,
          input: {
            isFlexible: isFlexible === "true",
            isCancelable: isCancelable === "true",
            luggageType: isFlight ? luggageType : null,
            ...rest
          },
          bookingIds
        }
      })
        .then(() => {
          form.setSubmitting(false);
          form.disabled = false;
          nav.unBlock("travel_request");
          showSuccess("Travel request sent");
          nav.close();
        })
        .catch((err) => {
          showError(`Cannot send travel request\nReason: ${err}`);
        });
    }
  });

  const addRide = (index: number) => {
    TravelRequest.InsertRideAfter(form.values, index);
  };

  const removeRide = (index: number) => {
    form.removeByIndex("rides", index);
  };

  const projectSelectorStore = useLocalStore(
    () =>
      new ProjectSelectorStore((id) => {
        form.setValue("costCenterId", id);
      })
  );

  useEffect(() => {
    const { bookingData } = localStore;

    if (isNew && bookingData?.consultant.TravelPreferences) {
      form.setValue("travelPreferences", bookingData?.consultant.TravelPreferences);
      form.resetToCurrent();
    }
  }, [localStore.bookingData]);

  const setConsultant = (consultant: ConsultantSelectorValue | null) => {
    form.setValue("consultantId", consultant ? consultant._id : null);

    if (consultant?.TravelPreferences) {
      form.setValue("travelPreferences", consultant.TravelPreferences);
    }
  };

  const setCostCenter = (costCenterId) => {
    form.setValue("costCenterId", costCenterId);
  };

  const setIsReschedule = (checked: boolean) => {
    form.setValue("isReschedule", checked);

    if (!checked) {
      form.setValue("rescheduleComment", "");
    }
  };

  const setTicketDifficultyLevel = (value: TicketDifficultyLevel) => {
    form.setValue("ticketDifficultyLevel", value);
  };

  const getConsultantValue = () => {
    const consultantId = form.values.consultantId;

    return consultantId
      ? {
          label: "",
          value: consultantId
        }
      : null;
  };

  const ticketDifficultyLevelOptions = Object.values(TicketDifficultyLevel).toLabelValue(
    (x) => locale.ticketDifficultyLevel.options[x]
  );

  const consultantValue = getConsultantValue();
  const consultantError = !isNew ? "" : form.getError("consultantId");
  const projectError = !isNew ? false : Boolean(form.getError("projectId"));
  const ticketDifficultyLevelError = !isNew
    ? false
    : Boolean(form.getError("ticketDifficultyLevel"));

  return {
    form,
    localStore,
    addRide,
    removeRide,
    projectSelectorStore,
    setConsultant,
    setCostCenter,
    setIsReschedule,
    consultantError,
    projectError,
    setTicketDifficultyLevel,
    ticketDifficultyLevelError,
    consultantValue,
    costCenterOptions,
    ticketDifficultyLevelOptions
  };
}
